import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { MODE_LIST, MODE_SEARCH, PlanPickerContext } from './usePlanPickerContext';
import { usePlanPickerApi } from '../usePlanPickerApi';
import usePlanPickerSideEffects from '../usePlanPickerSideEffects';
import useDebouncedCallback from '../../../hooks/useDebouncedCallback';

const SEARCH_DEBOUNCE_MS = 500;

/**
 * @param {AsyncPlanPickerProviderProps} props
 */
function AsyncPlanPickerProvider({
    dataStrategy,
    action,
    apiParams,
    initialId,
    initialIndex,
    children
}) {
    const [selectedId, setSelectedId] = useState(initialId);
    const [
        { itemsById, list, search },
        {
            getListItems,
            getSearchItems,
            loadPageForSelectedItem,
            loadListItemsByIndex,
            loadInitialListData,
            loadInitialSearchData,
            loadSearchItemsByIndex,
            updateSearchQuery: updateSearchQueryImmediate
        }
    ] = usePlanPickerApi(apiParams);

    const { query: searchQuery } = search;
    const runSideEffectsOnChange = usePlanPickerSideEffects({ action });
    const selectRef = useRef(null);

    const mode = searchQuery ? MODE_SEARCH : MODE_LIST;
    const options = useMemo(
        () => (mode === MODE_SEARCH ? getSearchItems() : getListItems()),
        [getListItems, getSearchItems, mode]
    );
    const grandTotal = list.total;
    const total = mode === MODE_SEARCH ? search.total : list.total;
    const isTotalUnknown = total === 'unknown';

    useEffect(() => {
        if (!isTotalUnknown) return;

        const controller = new AbortController();
        if (mode === MODE_SEARCH) {
            loadInitialSearchData(controller.signal);
        } else {
            loadInitialListData(initialIndex, controller.signal);
        }

        // eslint-disable-next-line consistent-return
        return () => {
            controller.abort();
        };
    }, [initialIndex, isTotalUnknown, loadInitialListData, loadInitialSearchData, mode]);

    const value = itemsById[selectedId] ?? null;

    /** @type {(option: import('../usePlanPickerApi/items').PlanPickerItem) => void} */
    const onChange = useCallback(
        option => {
            setSelectedId(option.id);
            loadPageForSelectedItem(option.id);
            runSideEffectsOnChange(option);
        },
        [runSideEffectsOnChange, loadPageForSelectedItem]
    );

    const updateSearchQuery = useDebouncedCallback(updateSearchQueryImmediate, SEARCH_DEBOUNCE_MS);

    const contextValue = useMemo(
        () => ({
            dataStrategy,
            mode,
            options,
            total: grandTotal,
            isWaitingForInitialData: isTotalUnknown,
            loadOptionsByIndex:
                mode === MODE_SEARCH ? loadSearchItemsByIndex : loadListItemsByIndex,
            searchQuery,
            updateSearchQuery,
            selectedId,
            value,
            onChange,
            selectRef
        }),
        [
            dataStrategy,
            options,
            grandTotal,
            isTotalUnknown,
            mode,
            onChange,
            loadSearchItemsByIndex,
            loadListItemsByIndex,
            searchQuery,
            selectedId,
            updateSearchQuery,
            value
        ]
    );

    return <PlanPickerContext.Provider value={contextValue}>{children}</PlanPickerContext.Provider>;
}

AsyncPlanPickerProvider.propTypes = {
    dataStrategy: PropTypes.oneOf(['async']),
    action: PropTypes.object.isRequired,
    apiParams: PropTypes.shape({
        url: PropTypes.string.isRequired,
        token: PropTypes.string.isRequired
    }).isRequired,
    initialId: PropTypes.string,
    initialIndex: PropTypes.number,
    children: PropTypes.node
};

export default AsyncPlanPickerProvider;

/**
 * @typedef {object} AsyncPlanPickerProviderProps
 * @prop {'async'} dataStrategy
 * @prop {import('../usePlanPickerSideEffects').PlanChangeAction} action
 * @prop {import('../usePlanPickerApi').PlanPickerApiProps} apiParams
 * @prop {string} initialId
 * @prop {number} initialIndex
 * @prop {import('react').ReactNode} children
 */
